import { flattenCommitFiles } from "../../../shared/commitFileRows";
import { type ReactNode, useState } from "react";
import type { ChatEntryWithSubTables } from "../../../shared/aiSdkMessages";
import { resolveStepColor } from "../../../shared/colors";
import { displayStepConfig } from "../../../shared/stepConfig";
import {
  SUB_AGENT_WAIT_TOOLS,
  type SubAgentResult,
  type SubAgentStatus,
} from "../../../shared/subAgent";
import { queries } from "../../../shared/queries";
import { useQuery } from "@rocicorp/zero/react";
import {
  type StreamMessage,
  useStreamingText,
} from "../../hooks/useStreamingText";
import { branchRunChat } from "../../lib/branchRunChat";
import {
  findPendingSubAgentWait,
  getStatusState,
  type StatusState,
} from "../../lib/chatStatus";
import { ActivityAnimationContext } from "../ActivityAnimationContext";
import { ToolStatus } from "../Chat/ToolStatus";
import {
  CollapsibleHeaderInner,
  ExpandedContent,
  StaticHeader,
} from "../Chat/Expand";
import { TypewriterText } from "../Chat/TypewriterText";
import { FallbackHeader } from "../Chat/ToolUse";
import { ActivityEntry } from "../Chat/ToolUseGroup";
import { ActivityRow } from "../Chat/ActivityRow";
import type { ChatTool, ToolUseProps } from "../Chat/ChatTool";
import type { BashData } from "../../tools/Bash";
import { connectToAppActivity } from "../../tools/ConnectToApp";
import { pushLiveActivity } from "../../tools/PushLive";
import type { ChatProps } from "../Chat/chatTypes";
import {
  askQuestion,
  approvePlan,
  bash,
  copyStorageFileToStep,
  editFile,
  execute,
  getRunExecutions,
  getStepLastExecution,
  getStepTest,
  listVersions,
  pinStepTest,
  readFile,
  readSkillFile,
  readStorageFile,
  readVersionChat,
  runSkillScript,
  runStep,
  runStepTests,
  seedBranchStorage,
  skillTool,
  unpinStepTest,
  webFetch,
  webpageDebugActivity,
  webSearch,
  writeFile,
} from "../../tools";

const childTools = (
  stepColors: BashData,
  spaceId?: string
): ReadonlyMap<string, ChatTool> =>
  new Map(
    [
      askQuestion,
      approvePlan,
      bash(stepColors),
      connectToAppActivity("connectChatToApp", spaceId),
      connectToAppActivity("connectStepToApp", spaceId),
      copyStorageFileToStep(stepColors),
      editFile(stepColors),
      execute,
      getRunExecutions,
      getStepLastExecution,
      getStepTest,
      listVersions,
      pinStepTest,
      pushLiveActivity,
      readFile(stepColors),
      readSkillFile,
      readStorageFile,
      readVersionChat,
      runSkillScript,
      runStep,
      runStepTests,
      seedBranchStorage,
      skillTool,
      unpinStepTest,
      webFetch,
      ...webpageDebugActivity,
      webSearch,
      writeFile(stepColors),
    ].map((tool) => [tool.name, tool])
  );

type ChildChat = {
  id: string;
  respondingAt: number | null;
  lastLoopHeartbeatAt: number | null;
  entries: ChatProps["entries"];
};

type Agent = { workflowId: string; branchId: string | null };

function childEntries(
  chats: readonly ChildChat[],
  from: number,
  until: number | null
) {
  return [
    ...new Map(
      chats
        .flatMap((chat) => chat.entries as ChatEntryWithSubTables[])
        .filter(
          (entry) =>
            entry.createdAt >= from &&
            (until == null || entry.createdAt <= until) &&
            (entry.entryType === "AssistantMessage" ||
              entry.assistantReasoning ||
              entry.toolCall)
        )
        .map((entry) => [entry.id, entry])
    ).values(),
  ].sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id));
}

// A run keeps the chat it started in after the agent saves new commits, so the newest chat may not be running.
function runChat(chats: readonly ChildChat[]) {
  return branchRunChat(
    chats.at(-1),
    chats.map((chat) => ({ chats: chat }))
  );
}

// Tool input reaches the database only once the model finishes writing it, so live rows read the child's stream.
function useChildStream(chats: readonly ChildChat[], live: boolean) {
  const chat = runChat(chats);
  return useStreamingText(
    chat?.id ?? null,
    live && chat?.respondingAt != null && chat.lastLoopHeartbeatAt != null
  ).messages;
}

function ChildActivity({
  chats,
  tools,
  from,
  until,
  expanded,
  compact,
  fallback,
}: {
  chats: readonly ChildChat[];
  tools: ReadonlyMap<string, ChatTool>;
  from: number;
  until: number | null;
  expanded: boolean;
  compact: boolean;
  fallback?: ReactNode;
}) {
  const [expandedToolUse, setExpandedToolUse] = useState<string | null>(null);
  const responding = runChat(chats)?.respondingAt != null;
  const streams = useChildStream(chats, expanded && until == null);
  const entries = childEntries(chats, from, until);
  if (compact) {
    const current = entries.findLast((entry) => entry.toolCall);
    return current ? (
      <ActivityRow connected hasNext={false}>
        <StaticHeader>
          <CollapsibleHeaderInner>
            <ChildStep
              entry={current}
              stream={streams.get(current.id)}
              tools={tools}
            />
          </CollapsibleHeaderInner>
        </StaticHeader>
      </ActivityRow>
    ) : (
      fallback
    );
  }
  if (entries.length === 0) return fallback;
  return entries.map((entry, index) => (
    <ActivityEntry
      key={entry.id}
      entry={entry}
      live={until == null && responding && index === entries.length - 1}
      connected
      hasNext={index < entries.length - 1}
      stream={streams.get(entry.id)}
      interrupted={!responding && entry.toolCall?.completedAt == null}
      expandedToolUse={expandedToolUse}
      setExpandedToolUse={setExpandedToolUse}
      tools={tools}
    />
  ));
}

// One id for every step keeps the row mounted and lets its text morph instead of retyping from blank.
function ChildStep({
  entry,
  stream,
  tools,
}: {
  entry: ChatEntryWithSubTables;
  stream: StreamMessage | undefined;
  tools: ReadonlyMap<string, ChatTool>;
}) {
  const call = entry.toolCall!;
  const tool = tools.get(call.toolName);
  const Header = tool?.Header ?? FallbackHeader;
  return (
    <ActivityAnimationContext value={{ live: true, activityId: "current" }}>
      <Header
        toolUse={{
          id: entry.id,
          toolName: call.toolName,
          input:
            (entry.streaming ? stream?.toolInput : undefined) ?? call.input,
          output: call.output,
          isError: call.isError,
        }}
        result={
          call.completedAt != null
            ? { id: entry.id, output: call.output, isError: !!call.isError }
            : null
        }
        createdAt={entry.createdAt}
        data={tool?.data}
        interrupted={false}
      />
    </ActivityAnimationContext>
  );
}

function CurrentChildActivity({
  chats,
  tools,
  from,
  text,
}: {
  chats: readonly ChildChat[];
  tools: ReadonlyMap<string, ChatTool>;
  from: number;
  text: string;
}) {
  const streams = useChildStream(chats, true);
  const current = childEntries(chats, from, null).findLast(
    (entry) => entry.toolCall
  );
  return current ? (
    <ChildStep entry={current} stream={streams.get(current.id)} tools={tools} />
  ) : (
    <ToolStatus icon="3b-mark" text={text} pending />
  );
}

type RenderChildChats = (
  chats: readonly ChildChat[],
  tools: ReadonlyMap<string, ChatTool>
) => ReactNode;

function BranchChats({
  branchId,
  pushedFrom,
  tools,
  render,
}: {
  branchId: string;
  pushedFrom: string | null;
  tools: ReadonlyMap<string, ChatTool>;
  render: RenderChildChats;
}) {
  const [commits] = useQuery(queries.loadBranchChats({ branchId }));
  const chats = commits.flatMap((commit) =>
    commit.chats ? [commit.chats] : []
  );
  return render(
    pushedFrom
      ? chats.filter((chat) =>
          chat.entries.some(
            ({ userAction }) =>
              userAction?.actionType === "pushDraftToLive" &&
              "branchId" in userAction.metadata &&
              userAction.metadata.branchId === pushedFrom
          )
        )
      : chats,
    tools
  );
}

function LiveChats({
  workflowId,
  tools,
  render,
}: {
  workflowId: string;
  tools: ReadonlyMap<string, ChatTool>;
  render: RenderChildChats;
}) {
  const [live] = useQuery(queries.loadLiveChat({ workflowId }));
  return render(live?.chat ? [live.chat] : [], tools);
}

function AgentChats({
  agent,
  render,
}: {
  agent: Agent;
  render: RenderChildChats;
}) {
  const [draft, draftResult] = useQuery(
    queries.workflowBranchSnapshotRoutes({
      workflowId: agent.workflowId,
      branchId: agent.branchId ?? undefined,
    })
  );
  // Pushing a draft live copies its chat history into a main chat marked with the draft’s id, then deletes the draft.
  const pushed =
    !!agent.branchId && draftResult.type === "complete" && !draft?.branches;
  const [main] = useQuery(
    queries.workflowBranchSnapshotRoutes({ workflowId: agent.workflowId }),
    { enabled: pushed }
  );
  const workflow = pushed ? main : draft;
  const branchId = pushed ? main?.branches?.id : agent.branchId;
  const headCommit = workflow?.branches?.headCommit;
  const stepColors = new Map(
    (headCommit ? flattenCommitFiles(headCommit) : []).flatMap((file) => {
      const dir = file.path.split("/")[0]!;
      return file.blob
        ? [
            [
              dir,
              resolveStepColor(
                displayStepConfig(file.blob.content).color ?? "",
                dir
              ),
            ] as const,
          ]
        : [];
    })
  );
  const tools = childTools(
    { getStepColor: (name) => stepColors.get(name) },
    workflow?.spaceId
  );
  if (branchId)
    return (
      <BranchChats
        branchId={branchId}
        pushedFrom={pushed ? agent.branchId : null}
        tools={tools}
        render={render}
      />
    );
  if (agent.branchId) return render([], tools);
  return (
    <LiveChats workflowId={agent.workflowId} tools={tools} render={render} />
  );
}

function WorkingLine({
  agent,
  from,
  text,
}: {
  agent: Agent;
  from: number;
  text: string;
}) {
  return (
    <AgentChats
      agent={agent}
      render={(chats, tools) => (
        <CurrentChildActivity
          chats={chats}
          tools={tools}
          from={from}
          text={text}
        />
      )}
    />
  );
}

function AgentActivity({
  agent,
  from,
  until,
  expanded,
  compact,
  fallback,
}: {
  agent: Agent;
  from: number;
  until: number | null;
  expanded: boolean;
  compact: boolean;
  fallback?: ReactNode;
}) {
  return (
    <AgentChats
      agent={agent}
      render={(chats, tools) => (
        <ChildActivity
          chats={chats}
          tools={tools}
          from={from}
          until={until}
          expanded={expanded}
          compact={compact}
          fallback={fallback}
        />
      )}
    />
  );
}

// A chat waiting on a sub-agent shows the sub-agent’s own thinking and building,
// matching what the workflow builder shows for the same run.
export function useSubAgentStatus(
  chatId: string,
  entries: ChatProps["entries"]
): StatusState | null {
  const pending = findPendingSubAgentWait(entries as ChatEntryWithSubTables[]);
  const [agents] = useQuery(queries.subAgentsForChat({ chatId }));
  const agent = pending
    ? agents.find((agent) => agent.awaitingEntryId === pending.id)
    : undefined;
  const [commits] = useQuery(
    queries.loadBranchChats({ branchId: agent?.branchId ?? "" }),
    { enabled: !!agent?.branchId }
  );
  const [live] = useQuery(
    queries.loadLiveChat({ workflowId: agent?.workflowId ?? "" }),
    { enabled: !!agent && !agent.branchId }
  );
  const chats = agent?.branchId
    ? commits.flatMap((commit) => (commit.chats ? [commit.chats] : []))
    : live?.chat
      ? [live.chat]
      : [];
  const runChatId = runChat(chats)?.id;
  const chat = chats.find((chat) => chat.id === runChatId);
  if (!chat) return null;
  const status = getStatusState(
    chat.entries as ChatEntryWithSubTables[],
    chat.respondingAt != null,
    "idle",
    false
  );
  return status === "thinking" || status === "building" || status === "loading"
    ? status
    : null;
}

function useToolAgent({ toolUse, result, data, interrupted }: ToolUseProps) {
  const [agents] = useQuery(
    queries.subAgentsForChat({ chatId: data as string })
  );
  const input = toolUse.input as { workflowId?: string };
  const output = result?.output as
    | Partial<SubAgentResult>
    | { status: "running"; workflowId: string }
    | undefined;
  const agent =
    agents.find((agent) => agent.awaitingEntryId === toolUse.id) ??
    agents.find(
      (agent) => agent.workflowId === (output?.workflowId ?? input.workflowId)
    );
  // A completed call reports "running" when it steered an agent whose earlier call receives the result.
  const status: SubAgentStatus | "sent" = result
    ? output?.status === "running"
      ? "sent"
      : (output?.status ?? (result.isError ? "failed" : "idle"))
    : agent?.awaitingEntryId === toolUse.id
      ? agent.status
      : interrupted
        ? "stopped"
        : "running";
  return { agent, status };
}

function SubAgentToolHeader(props: ToolUseProps) {
  const { agent, status } = useToolAgent(props);
  const input = props.toolUse.input as {
    name?: string;
    activeDescription?: string;
    completedDescription?: string;
    failedDescription?: string;
  };
  const name = agent?.workflow?.name ?? input.name ?? "workflow";
  const text = {
    running: input.activeDescription
      ? `${input.activeDescription}…`
      : `Working on ${name}…`,
    needs_input: `${name} needs input`,
    idle: input.completedDescription ?? `Worked on ${name}`,
    failed: input.failedDescription ?? `${name} failed`,
    stopped: `Stopped ${name}`,
    sent: `Sent a message to ${name}`,
  }[status];
  // Expanded, the activity below already ends with the current step.
  if (
    agent &&
    (status === "running" || status === "needs_input") &&
    !props.interrupted &&
    !props.expanded
  )
    return <WorkingLine agent={agent} from={props.createdAt} text={text} />;
  return (
    <ToolStatus
      icon="3b-mark"
      text={text}
      pending={status === "running" && !props.interrupted}
    />
  );
}

function SubAgentToolBody(props: ToolUseProps) {
  const { agent } = useToolAgent(props);
  const output = props.result?.output as Partial<SubAgentResult> | undefined;
  const answered = output && "answered" in output ? output.answered : undefined;
  // A push drops the draft from the result, but its history is found through the draft it was answered on.
  const conversation = output?.workflowId
    ? {
        workflowId: output.workflowId,
        branchId: output.branchId ?? answered?.branchId ?? null,
      }
    : agent;
  if (!conversation) return null;
  // An answered pause carries a server-written summary for the parent model, not the agent’s own words.
  const summary =
    output && "summary" in output && !answered ? output.summary : undefined;
  return (
    <AgentActivity
      agent={conversation}
      from={props.createdAt}
      until={props.result ? (props.completedAt ?? null) : null}
      expanded={!!props.expanded}
      compact={!!props.compact}
      fallback={
        summary ? (
          <ExpandedContent>
            <TypewriterText text={summary} variant="text-small" />
          </ExpandedContent>
        ) : null
      }
    />
  );
}

export function subAgentTools(chatId: string): ChatTool[] {
  return [...SUB_AGENT_WAIT_TOOLS].map((name) => ({
    name,
    Header: SubAgentToolHeader,
    Body: SubAgentToolBody,
    data: chatId,
    expandWhilePending: true,
    alwaysLoadOutput: true,
  }));
}
